import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, User, Mail, Lock, Phone, ArrowRight, ChefHat, CheckCircle2 } from 'lucide-react';
import { usePageViewTracking, useTracking } from '../hooks/useTracking';
import { Logo } from '../components/Logo';
import { formatErrorMessage } from '../utils/errorFormatter';

export default function RegisterPage() {
  const [form, setForm] = useState({
    restaurant_name: '', first_name: '', last_name: '',
    email: '', password: '', confirm_password: '', phone: ''
  });
  const [loading, setLoading] = useState(false);
  const { registerRestaurant } = useAuth();
  const navigate = useNavigate();
  const { trackEvent } = useTracking();
  
  // Track page view
  usePageViewTracking('signup');
  
  // Track signup started when page loads
  useEffect(() => {
    trackEvent('signup_started');
  }, [trackEvent]);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    // Track signup step: form submitted
    trackEvent('signup_step', { step: 'form_submitted', email: form.email });
    
    setLoading(true);
    try {
      await registerRestaurant({
        restaurant_name: form.restaurant_name,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
        phone: form.phone
      });
      
      // Track signup completed
      trackEvent('signup_completed', { 
        restaurant_name: form.restaurant_name,
        email: form.email 
      });
      
      toast.success(`Bienvenue ! Votre espace ${form.restaurant_name} est prêt.`);
      // Hard redirect to bypass PublicRoute re-render race condition
      window.location.href = '/onboarding';
    } catch (err) {
      trackEvent('signup_error', { error: err.response?.data?.detail || 'Unknown error' });
      toast.error(formatErrorMessage(err, 'Erreur lors de la création du compte'));
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Gestion des commandes fournisseurs',
    'Envoi automatique des bons de commande par email',
    'Contrôle qualité des livraisons',
    'Tableau de bord et statistiques',
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1B4332] flex-col justify-between p-12">
        <div>
          <Logo variant="white" size="large" showText={true} />
        </div>
        <div className="text-white">
          <h2 className="text-3xl font-bold mb-4">Gérez vos approvisionnements<br/>comme un pro.</h2>
          <p className="text-green-200 mb-8 text-lg">Rejoignez les restaurateurs qui font confiance à OrderLab pour simplifier leurs commandes fournisseurs.</p>
          <div className="space-y-3">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="text-green-100">{b}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 p-4 bg-white/10 rounded-xl">
            <p className="text-green-200 text-sm font-medium mb-1">Plan gratuit inclus</p>
            <p className="text-white text-sm">Démarrez gratuitement avec 3 fournisseurs, 5 utilisateurs et 30 commandes/mois.</p>
          </div>
        </div>
        <p className="text-green-200 text-sm">© 2024 OrderLab — Tous droits réservés</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex justify-center mb-8">
            <Logo size="default" showText={true} />
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Créer votre espace restaurant</h1>
              <p className="text-gray-500 mt-1 text-sm">Gratuit, sans carte bancaire</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-700 font-medium">Nom du restaurant *</Label>
                <div className="relative mt-1">
                  <Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    name="restaurant_name" value={form.restaurant_name}
                    onChange={handleChange} placeholder="Le Bistro de la Place" required
                    className="pl-10"
                    data-testid="register-restaurant-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-700 font-medium">Prénom *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input name="first_name" value={form.first_name} onChange={handleChange}
                      placeholder="Jean" required className="pl-10" data-testid="register-first-name" />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Nom *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input name="last_name" value={form.last_name} onChange={handleChange}
                      placeholder="Dupont" required className="pl-10" data-testid="register-last-name" />
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input name="email" type="email" value={form.email} onChange={handleChange}
                    placeholder="jean@monrestaurant.fr" required className="pl-10" data-testid="register-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-700 font-medium">Mot de passe *</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input name="password" type="password" value={form.password} onChange={handleChange}
                      placeholder="Min. 6 caractères" required className="pl-10" data-testid="register-password" />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Confirmer *</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input name="confirm_password" type="password" value={form.confirm_password}
                      onChange={handleChange} placeholder="Répéter" required className="pl-10"
                      data-testid="register-confirm-password" />
                  </div>
                </div>
              </div>
              <Button
                type="submit" disabled={loading}
                className="w-full bg-[#1B4332] hover:bg-[#152e22] text-white h-11 text-base"
                data-testid="register-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />Création...</span>
                ) : (
                  <span className="flex items-center gap-2">Créer mon espace <ArrowRight className="w-4 h-4" /></span>
                )}
              </Button>
            </form>
            <p className="text-center text-sm text-gray-500 mt-4">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-[#1B4332] font-medium hover:underline">Se connecter</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
